import React, { useState, useEffect } from 'react';
import { Table, Button, Form, Row, Col, Badge, Alert, Modal, Pagination } from 'react-bootstrap';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

interface Transaction {
  _id: string;
  userId: {
    _id: string;
    name: string;
    email: string;
  };
  type: string;
  amount: number;
  description: string;
  createdAt: string;
  status?: string;
  metadata?: {
    adminName?: string;
    adminEmail?: string;
    reason?: string;
    balanceBefore?: number;
    balanceAfter?: number;
  };
}

interface TransactionStats {
  totalTransactions: number;
  totalAmount: number;
  byType: {
    [key: string]: {
      count: number;
      amount: number;
    };
  };
}

const TransactionManagement: React.FC = () => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [filteredTransactions, setFilteredTransactions] = useState<Transaction[]>([]);
  const [stats, setStats] = useState<TransactionStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [paginatedTransactions, setPaginatedTransactions] = useState<Transaction[]>([]);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Modal
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchTransactions();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [transactions, searchTerm, typeFilter, dateFrom, dateTo]);

  useEffect(() => {
    // Reset to first page if filters change
    setCurrentPage(1);
  }, [filteredTransactions.length]);

  useEffect(() => {
    // Update paginated transactions when filteredTransactions or currentPage/pageSize changes
    const startIdx = (currentPage - 1) * pageSize;
    const endIdx = startIdx + pageSize;
    setPaginatedTransactions(filteredTransactions.slice(startIdx, endIdx));
  }, [filteredTransactions, currentPage, pageSize]);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      console.log('TransactionManagement - Fetching transactions...');
      const response = await axios.get(`${API_BASE_URL}/admin/reward-transactions?limit=1000`, config);
      console.log('TransactionManagement - Response:', response.data);
      
      const transactionsData = Array.isArray(response.data) ? response.data : response.data.transactions || [];
      setTransactions(transactionsData);
      
      // Calculate stats
      calculateStats(transactionsData);
      
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setError('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (transactions: Transaction[]) => {
    const totalTransactions = transactions.length;
    const totalAmount = transactions.reduce((sum, t) => sum + t.amount, 0);
    
    const byType: { [key: string]: { count: number; amount: number } } = {};
    transactions.forEach(t => {
      if (!byType[t.type]) {
        byType[t.type] = { count: 0, amount: 0 };
      }
      byType[t.type].count++;
      byType[t.type].amount += t.amount;
    });

    setStats({ totalTransactions, totalAmount, byType });
  };

  const applyFilters = () => {
    let filtered = [...transactions];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(t =>
        t.userId?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.userId?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Type filter
    if (typeFilter) {
      filtered = filtered.filter(t => t.type === typeFilter);
    }

    // Date filters
    if (dateFrom) {
      filtered = filtered.filter(t => new Date(t.createdAt) >= new Date(dateFrom));
    }
    if (dateTo) {
      const toDate = new Date(dateTo);
      toDate.setHours(23, 59, 59, 999); // Include the entire day
      filtered = filtered.filter(t => new Date(t.createdAt) <= toDate);
    }

    setFilteredTransactions(filtered);
  };

  const formatCurrency = (amount: number) => `₹${amount.toFixed(2)}`;

  const getTransactionTypeLabel = (type: string) => {
    const types: { [key: string]: { label: string; color: string; icon: string } } = {
      'task_reward': { label: 'Task Rewards', color: 'success', icon: 'fa-check-circle' },
      'referral_signup_bonus': { label: 'Referral Bonus', color: 'info', icon: 'fa-user-plus' },
      'commission_earning': { label: 'Commission', color: 'primary', icon: 'fa-percentage' },
      'signup_bonus': { label: 'Welcome Bonus', color: 'warning', icon: 'fa-gift' },
      'manual_bonus': { label: 'Admin Bonus', color: 'secondary', icon: 'fa-hand-holding-usd' },
      'withdrawal': { label: 'Withdrawal', color: 'danger', icon: 'fa-money-bill-wave' },
      'upgrade_commission_l1': { label: 'Referral Bonus L1', color: 'success', icon: 'fa-arrow-up' },
      'upgrade_commission_l2': { label: 'Referral Bonus L2', color: 'info', icon: 'fa-arrow-up' },
      'upgrade_commission_l3': { label: 'Referral Bonus L3', color: 'warning', icon: 'fa-arrow-up' },
      'upgrade_commission_l4': { label: 'Referral Bonus L4', color: 'primary', icon: 'fa-arrow-up' }
    };
    return types[type] || { label: type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()), color: 'dark', icon: 'fa-question' };
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const exportTransactions = () => {
    const csvData = [
      ['Date', 'User', 'Email', 'Type', 'Amount', 'Description', 'Admin'],
      ...filteredTransactions.map(t => [
        formatDate(t.createdAt),
        t.userId?.name || 'Unknown',
        t.userId?.email || 'No email',
        t.type,
        t.amount.toString(),
        t.description,
        t.metadata?.adminName || 'System'
      ])
    ];

    const csvContent = csvData.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transactions_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setTypeFilter('');
    setDateFrom('');
    setDateTo('');
  };

  if (loading) {
    return (
      <div className="admin-card">
        <div className="admin-card-body text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>

          {/* ...existing code... */}
                  {/* Pagination Controls (always visible) */}
                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <div>
                      <Form.Select
                        style={{ width: 120, display: 'inline-block' }}
                        value={pageSize}
                        onChange={e => {
                          setPageSize(Number(e.target.value));
                          setCurrentPage(1);
                        }}
                        size="sm"
                      >
                        {[5, 10, 20, 50, 100].map(size => (
                          <option key={size} value={size}>{size} / page</option>
                        ))}
                      </Form.Select>
                    </div>
                    <div>
                      <Pagination className="mb-0">
                        <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                        <Pagination.Prev onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} />
                        {(() => {
                          const totalPages = Math.ceil(filteredTransactions.length / pageSize);
                          let start = Math.max(1, currentPage - 2);
                          let end = Math.min(totalPages, currentPage + 2);
                          if (end - start < 4) {
                            if (start === 1) end = Math.min(totalPages, start + 4);
                            if (end === totalPages) start = Math.max(1, end - 4);
                          }
                          return Array.from({ length: end - start + 1 }, (_, i) => start + i).map(page => (
                            <Pagination.Item
                              key={page}
                              active={page === currentPage}
                              onClick={() => setCurrentPage(page)}
                            >
                              {page}
                            </Pagination.Item>
                          ));
                        })()}
                        <Pagination.Next onClick={() => setCurrentPage(p => Math.min(Math.ceil(filteredTransactions.length / pageSize), p + 1))} disabled={currentPage === Math.ceil(filteredTransactions.length / pageSize) || filteredTransactions.length === 0} />
                        <Pagination.Last onClick={() => setCurrentPage(Math.ceil(filteredTransactions.length / pageSize))} disabled={currentPage === Math.ceil(filteredTransactions.length / pageSize) || filteredTransactions.length === 0} />
                      </Pagination>
                    </div>
                  </div>
        </div>
      </div>
      </div>
    );
  }

  return (
    <div className="transaction-management">
      <div className="admin-card">
        <div className="admin-card-header">
          <h5 className="mb-0">
            <i className="fas fa-exchange-alt me-2"></i>Transaction Management
          </h5>
          <p className="mb-0 text-muted">
            <small>Monitor and analyze all reward transactions across the platform</small>
          </p>
        </div>
        
        <div className="admin-card-body">
          {/* Help Section */}
          <Alert variant="info" className="mb-4">
            <Alert.Heading as="h6">
              <i className="fas fa-info-circle me-2"></i>Transaction Management Guide
            </Alert.Heading>
            <ul className="mb-0 small">
              <li><strong>View All Transactions:</strong> Monitor task rewards, referral bonuses, manual bonuses, and withdrawals</li>
              <li><strong>Filter & Search:</strong> Use filters to find specific transactions by user, type, or date range</li>
              <li><strong>Export Data:</strong> Download transaction reports for accounting and analysis</li>
              <li><strong>Transaction Details:</strong> Click on any transaction to view detailed metadata</li>
            </ul>
          </Alert>
        </div>
      </div>
      
      <div className="admin-card">
        <div className="admin-card-header">
          <h5 className="mb-0"><i className="fas fa-exchange-alt me-2"></i>Transaction Management</h5>
        </div>
        
        <div className="admin-card-body">
          {error && (
            <Alert variant="danger" className="mb-3">
              <i className="fas fa-exclamation-triangle me-2"></i>
              {error}
            </Alert>
          )}

          {/* Statistics */}
          {stats && (
            <Row className="mb-4">
              <Col md={3}>
                <div className="admin-card bg-primary text-white">
                  <div className="admin-card-body text-center">
                    <i className="fas fa-receipt fa-2x mb-2"></i>
                    <h4 className="mb-0">{stats.totalTransactions.toLocaleString()}</h4>
                    <small>Total Transactions</small>
                  </div>
                </div>
              </Col>
              <Col md={3}>
                <div className="admin-card bg-success text-white">
                  <div className="admin-card-body text-center">
                    <i className="fas fa-coins fa-2x mb-2"></i>
                    <h4 className="mb-0">{formatCurrency(stats.totalAmount)}</h4>
                    <small>Total Amount</small>
                  </div>
                </div>
              </Col>
              <Col md={3}>
                <div className="admin-card bg-info text-white">
                  <div className="admin-card-body text-center">
                    <i className="fas fa-filter fa-2x mb-2"></i>
                    <h4 className="mb-0">{filteredTransactions.length.toLocaleString()}</h4>
                    <small>Filtered Results</small>
                  </div>
                </div>
              </Col>
              <Col md={3}>
                <div className="admin-card bg-warning text-white">
                  <div className="admin-card-body text-center">
                    <i className="fas fa-calendar fa-2x mb-2"></i>
                    <h4 className="mb-0">{Object.keys(stats.byType).length}</h4>
                    <small>Transaction Types</small>
                  </div>
                </div>
              </Col>
            </Row>
          )}

          {/* Filters */}
          <Row className="mb-3">
            <Col md={3}>
              <Form.Group>
                <Form.Label>Search</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Search by user or description..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>Type</Form.Label>
                <Form.Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                  <option value="">All Types</option>
                  <option value="task_reward">Task Completion Rewards</option>
                  <option value="referral_signup_bonus">Referral Signup Bonuses</option>
                  <option value="commission_earning">Commission Earnings</option>
                  <option value="signup_bonus">Welcome Bonuses</option>
                  <option value="manual_bonus">Manual Admin Bonuses</option>
                  <option value="withdrawal">Withdrawals</option>
                  <option value="referral">Referral</option>
                  <option value="task_completion">Task Completion</option>
                  <option value="withdrawal">Withdrawal</option>
                  <option value="penalty">Penalty</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>From Date</Form.Label>
                <Form.Control
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>To Date</Form.Label>
                <Form.Control
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={3} className="d-flex align-items-end gap-2">
              <Button variant="secondary" onClick={clearFilters}>
                <i className="fas fa-times me-1"></i>Clear
              </Button>
              <Button variant="primary" onClick={fetchTransactions}>
                <i className="fas fa-sync me-1"></i>Refresh
              </Button>
              <Button variant="success" onClick={exportTransactions}>
                <i className="fas fa-download me-1"></i>Export
              </Button>
            </Col>
          </Row>

          {/* Transactions Table */}
          <div className="table-responsive">
            <Table striped hover>
              <thead>
                <tr>
                  <th>Date & Time</th>
                  <th>User</th>
                  <th>Type</th>
                  <th>Amount</th>
                  <th>Description</th>
                  <th>Admin/Source</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTransactions.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-4">
                      <div>
                        <i className="fas fa-receipt text-muted fa-2x mb-2"></i>
                        <div>No transactions found.</div>
                        <small className="text-muted">Try adjusting your filters or add some transactions.</small>
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginatedTransactions.map((transaction) => (
                    <tr key={transaction._id}>
                      <td>
                        <small>{formatDate(transaction.createdAt)}</small>
                      </td>
                      <td>
                        <div>
                          <strong>{transaction.userId?.name || 'Unknown User'}</strong>
                          <br />
                          <small className="text-muted">{transaction.userId?.email || 'No email'}</small>
                        </div>
                      </td>
                      <td>
                        {(() => {
                          const typeInfo = getTransactionTypeLabel(transaction.type);
                          return (
                            <Badge bg={typeInfo.color}>
                              <i className={`fas ${typeInfo.icon} me-1`}></i>
                              {typeInfo.label}
                            </Badge>
                          );
                        })()}
                      </td>
                      <td>
                        <span className={`fw-bold ${transaction.amount >= 0 ? 'text-success' : 'text-danger'}`}>
                          {transaction.amount >= 0 ? '+' : ''}{formatCurrency(transaction.amount)}
                        </span>
                      </td>
                      <td>
                        <span title={transaction.description}>
                          {transaction.description?.length > 50 
                            ? transaction.description.substring(0, 50) + '...' 
                            : transaction.description}
                        </span>
                      </td>
                      <td>
                        <small className="text-muted">
                          {transaction.metadata?.adminName || 'System'}
                        </small>
                      </td>
                      <td>
                        <Button
                          size="sm"
                          variant="outline-info"
                          onClick={() => {
                            setSelectedTransaction(transaction);
                            setShowModal(true);
                          }}
                        >
                          <i className="fas fa-eye"></i>
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>

          {/* Pagination Controls (always visible, below table) */}
          <div className="d-flex justify-content-between align-items-center mt-3 mb-2">
            <div>
              <Form.Select
                style={{ width: 120, display: 'inline-block' }}
                value={pageSize}
                onChange={e => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                size="sm"
              >
                {[5, 10, 20, 50, 100].map(size => (
                  <option key={size} value={size}>{size} / page</option>
                ))}
              </Form.Select>
            </div>
            <div>
              <Pagination className="mb-0">
                <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                <Pagination.Prev onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} />
                {(() => {
                  const totalPages = Math.ceil(filteredTransactions.length / pageSize);
                  let start = Math.max(1, currentPage - 2);
                  let end = Math.min(totalPages, currentPage + 2);
                  if (end - start < 4) {
                    if (start === 1) end = Math.min(totalPages, start + 4);
                    if (end === totalPages) start = Math.max(1, end - 4);
                  }
                  return Array.from({ length: end - start + 1 }, (_, i) => start + i).map(page => (
                    <Pagination.Item
                      key={page}
                      active={page === currentPage}
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </Pagination.Item>
                  ));
                })()}
                <Pagination.Next onClick={() => setCurrentPage(p => Math.min(Math.ceil(filteredTransactions.length / pageSize), p + 1))} disabled={currentPage === Math.ceil(filteredTransactions.length / pageSize) || filteredTransactions.length === 0} />
                <Pagination.Last onClick={() => setCurrentPage(Math.ceil(filteredTransactions.length / pageSize))} disabled={currentPage === Math.ceil(filteredTransactions.length / pageSize) || filteredTransactions.length === 0} />
              </Pagination>
            </div>
          </div>
        </div>
      </div>

      {/* Transaction Details Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-receipt me-2"></i>Transaction Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedTransaction && (
            <div>
              <Row>
                <Col md={6}>
                  <strong>Transaction ID:</strong>
                  <p className="font-monospace">{selectedTransaction._id}</p>
                  
                  <strong>Date & Time:</strong>
                  <p>{formatDate(selectedTransaction.createdAt)}</p>
                  
                  <strong>Type:</strong>
                  <p>
                    {(() => {
                      const typeInfo = getTransactionTypeLabel(selectedTransaction.type);
                      return (
                        <Badge bg={typeInfo.color}>
                          <i className={`fas ${typeInfo.icon} me-1`}></i>
                          {typeInfo.label}
                        </Badge>
                      );
                    })()}
                  </p>
                  
                  <strong>Amount:</strong>
                  <p className={`fw-bold fs-5 ${selectedTransaction.amount >= 0 ? 'text-success' : 'text-danger'}`}>
                    {selectedTransaction.amount >= 0 ? '+' : ''}{formatCurrency(selectedTransaction.amount)}
                  </p>
                </Col>
                <Col md={6}>
                  <strong>User:</strong>
                  <p>{selectedTransaction.userId?.name || 'Unknown User'}</p>
                  
                  <strong>Email:</strong>
                  <p>{selectedTransaction.userId?.email || 'No email'}</p>
                  
                  <strong>Description:</strong>
                  <p>{selectedTransaction.description}</p>
                  
                  <strong>Processed By:</strong>
                  <p>{selectedTransaction.metadata?.adminName || 'System'}</p>
                </Col>
              </Row>
              
              {selectedTransaction.metadata && (
                <div className="mt-3">
                  <strong>Additional Information:</strong>
                  <div className="bg-light p-3 rounded mt-2">
                    {selectedTransaction.metadata.reason && (
                      <p><strong>Reason:</strong> {selectedTransaction.metadata.reason}</p>
                    )}
                    {selectedTransaction.metadata.balanceBefore !== undefined && (
                      <p><strong>Balance Before:</strong> {formatCurrency(selectedTransaction.metadata.balanceBefore)}</p>
                    )}
                    {selectedTransaction.metadata.balanceAfter !== undefined && (
                      <p><strong>Balance After:</strong> {formatCurrency(selectedTransaction.metadata.balanceAfter)}</p>
                    )}
                    {selectedTransaction.metadata.adminEmail && (
                      <p><strong>Admin Email:</strong> {selectedTransaction.metadata.adminEmail}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default TransactionManagement;